import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from "react-redux";
import {FormattedMessage} from "react-intl";

import {Dialog, DialogTitle, DialogContent} from "@mui/material";

import Form from "views/categories/components/form";
import MoveDialog from "views/categories/components/moveDialog";

import {reset} from "actions/category/create";
import {categoryList} from "actions/category/list";
import {deleteCategory} from "actions/category/delete";

import {getTranslation} from "domain/helpers/translations";
import getTranslationInitialValues from "utils/getTranslationInitialValues";
import buildCategoryNamePath from "./buildCategoryNamePath";

CreateCategoryDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    initialName: PropTypes.string,
    onCreated: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onRolledBack: PropTypes.func.isRequired,
};

/**
 * Two-phase category quick-create flow: the creation form of the categories
 * page (category created at the root), then the existing move dialog to
 * optionally place it in the tree.
 *
 * Confirming the placement keeps the category and lets the caller map it
 * (`onClose` with the created category). Cancelling the move dialog (button or
 * click-outside) rolls the creation back: the just-created category is deleted
 * and, only once that succeeds, `onRolledBack` asks the caller to drop it from
 * the other selects — nothing is mapped. If the deletion is refused (e.g. the
 * member may create but not delete) nothing else changes and `onClose(null)`
 * just closes the flow, leaving the category untouched.
 *
 * `onClose` receives the created category (null when the flow ended without a
 * confirmed placement), so the caller can chain on it without tracking it.
 */
function CreateCategoryDialog({isOpen, initialName, onCreated, onClose, onRolledBack, created, deleted, deleteError, allCategories, reset, categoryList, deleteCategory}) {
    const [createdCategory, setCreatedCategory] = useState(null);
    const [isMoveOpen, setIsMoveOpen] = useState(false);
    const [isRollingBack, setIsRollingBack] = useState(false);
    // parent chosen in the move dialog, captured synchronously so the confirmed
    // placement can be turned into a full name path
    const placedParent = useRef(null);

    useEffect(() => {
        if (isOpen) {
            // clear any leftover from the categories page and load the tree
            // for the move phase
            reset();
            setCreatedCategory(null);
            setIsMoveOpen(false);
            setIsRollingBack(false);
            placedParent.current = null;
            categoryList();
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && created && !createdCategory) {
            setCreatedCategory(created);
            onCreated(created);
        }
    }, [created]);

    // MoveDialog is mounted closed first because it calls closeDialog() on
    // mount; it is opened on the next render cycle
    useEffect(() => {
        if (createdCategory) {
            setIsMoveOpen(true);
        }
    }, [createdCategory]);

    // resolve a rollback started on cancel: remove the category from the other
    // selects only once its deletion actually succeeded; on failure leave
    // everything as-is and just close the flow
    useEffect(() => {
        if (!isRollingBack) {
            return;
        }

        if (deleted && createdCategory && deleted.id === createdCategory.id) {
            setIsRollingBack(false);
            onRolledBack(createdCategory);
        } else if (deleteError) {
            setIsRollingBack(false);
            onClose(null);
        }
    }, [deleted, deleteError]);

    function handleMoveDialogClose() {
        // ignore the closeDialog() MoveDialog fires when it mounts; a real
        // closeDialog() while open means the placement was confirmed
        if (!isMoveOpen) {
            return;
        }

        setIsMoveOpen(false);

        // rebuild the full "parent > … > leaf" path from the chosen parent so
        // the mapped row shows it immediately (the backend, which is the only
        // other source of the path, maps asynchronously)
        const namePath = buildCategoryNamePath(
            placedParent.current,
            getTranslation(createdCategory).name,
            (category) => getTranslation(category).name
        );

        onClose(createdCategory, namePath);
    }

    function handleMoveDialogCancel() {
        setIsMoveOpen(false);

        if (createdCategory) {
            setIsRollingBack(true);
            deleteCategory(createdCategory);
        } else {
            onClose(null);
        }
    }

    return (
        <>
            <Dialog
                open={isOpen && null === createdCategory}
                onClose={() => onClose(null)}
                maxWidth={'sm'}
                scroll={'body'}
            >
                <DialogTitle>
                    <FormattedMessage id={'category.form.create.title'} />
                </DialogTitle>
                <DialogContent>
                    {isOpen && null === createdCategory && (
                        <Form
                            closeDialog={() => onClose(null)}
                            initialValues={{translations: getTranslationInitialValues(initialName ?? '', 'name')}}
                        />
                    )}
                </DialogContent>
            </Dialog>
            {isOpen && null !== createdCategory && (
                <MoveDialog
                    isOpen={isMoveOpen}
                    closeDialog={() => handleMoveDialogClose()}
                    onCancel={() => handleMoveDialogCancel()}
                    onConfirm={(parent) => { placedParent.current = parent; }}
                    categorySelected={createdCategory}
                    allCategories={allCategories ?? []}
                />
            )}
        </>
    );
}

const mapStateToProps = state => {
    return {
        created: state.category.create.created,
        deleted: state.category.deleteCategory.deleted,
        deleteError: state.category.deleteCategory.error,
        allCategories: state.category.list.retrieved,
    }
};

const mapDispatchToProps = dispatch => ({
    reset: () => dispatch(reset()),
    categoryList: () => dispatch(categoryList()),
    deleteCategory: (item) => dispatch(deleteCategory(item)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(CreateCategoryDialog);
